import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getCustomerById } from "../../services/customerService";
import { createPayment } from "../../services/paymentService";
import PaymentHistory from "../Payment/PaymentHistory";
import { getCustomerGroups } from "../../services/customerGroupService"; // ✅ Import Group Service

const CustomerProfile = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // ✅ Immediately set updated customer if available
  const [customer, setCustomer] = useState(location.state?.customer || null);

  const [loading, setLoading] = useState(!customer);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().split("T")[0] // Default to today's date
  );
  const [paymentMode, setPaymentMode] = useState("Cash"); // Default payment mode
  const [isPayEnabled, setIsPayEnabled] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [groups, setGroups] = useState([]); // ✅ State to store group data

  // ✅ Fetch customer details if not available in location.state
  useEffect(() => {
    if (customer) {
      fetchCustomerDetails();
    }
    fetchGroups(); // ✅ Fetch groups list
  }, [id]);

  // ✅ Detect updatedCustomer and update state when navigating back
  useEffect(() => {
    if (location.state?.updatedCustomer) {
      console.log(
        "🔄 Updating Customer from Location State:",
        location.state.updatedCustomer
      );
      setCustomer(location.state.updatedCustomer);
    }
  }, [location.state?.updatedCustomer]); // Re-run when updatedCustomer changes

  const fetchCustomerDetails = async () => {
    setLoading(true);
    try {
      const response = await getCustomerById(id);
      setCustomer(response.data);
    } catch (error) {
      console.error("Error fetching customer details:", error.message);
      toast.error("Failed to load customer details");
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await getCustomerGroups();
      setGroups(response.data);
    } catch (error) {
      console.error("Error fetching groups:", error.message);
      toast.error("Failed to load groups.");
    }
  };

  // ✅ Function to get group name based on group_id
  const getGroupName = (groupId) => {
    const group = groups.find((g) => g.group_id === groupId);
    return group ? group.group_name : "N/A"; // Return group name or 'N/A' if not found
  };

  const handleEditProfile = () => {
    navigate(`/edit-customer/${customer.id}`, { state: { customer } });
  };

  const handlePaymentChange = (e) => {
    const value = e.target.value;
    if (!isNaN(value) && value > 0) {
      setPaymentAmount(value);
      setIsPayEnabled(true);
    } else {
      setPaymentAmount("");
      setIsPayEnabled(false);
    }
  };

  const handlePayDue = async () => {
    if (!customer) {
      toast.error("Customer details not loaded.");
      return;
    }

    const paymentData = {
      customer_id: customer.id,
      payment_date: paymentDate,
      amount_paid: parseFloat(paymentAmount),
      payment_mode: paymentMode, // Use selected payment mode
      notes: "Manual payment submission",
    };

    try {
      const response = await createPayment(paymentData);

      toast.success(`Payment of ₹${paymentAmount} processed successfully!`);

      setCustomer((prev) => ({
        ...prev,
        due_amount: Math.max(0, prev.due_amount - parseFloat(paymentAmount)),
      }));
      console.log("Payment response:", response);
      setPaymentAmount("");
      setPaymentMode("Cash"); // Reset payment mode to default
      setPaymentDate(new Date().toISOString().split("T")[0]); // Reset to today's date
      setShowPaymentHistory(true); // Show payment history after successful payment
      setIsPayEnabled(false);
    } catch (error) {
      console.error("Error processing payment:", error.message);
      if (error.response) {
        toast.error(error.message);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-red-500 text-lg">Customer not found.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center p-0.5">
      {/* Header */}
      <div className="w-full bg-gradient-to-r from-blue-500 to-blue-400 text-white p-1 rounded-b-xl shadow-lg flex justify-between items-center px-4">
        <h2 className="text-2xl capitalize font-semibold">
          {customer.name}: {customer.delivery_schedule}
        </h2>
        <button
          onClick={handleEditProfile}
          className="bg-white text-blue-500 px-4 py-2 rounded-full text-sm font-semibold hover:bg-gray-200 transition duration-200"
        >
          ✏️ Edit
        </button>
      </div>

      {/* Customer Info Card */}
      <div className="w-full max-w-md capitalize bg-white shadow-md rounded-2xl p-3 mt-2">
        <h3 className="text-lg font-bold text-gray-800 mb-2">Customer Info</h3>
        <div className="space-y-4">
          <div className="flex justify-between">
            <span className="text-gray-500 font-medium">Mobile:</span>
            <span className="text-gray-700 font-medium">{customer.mobile}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500 font-medium">Address:</span>
            <span className="text-gray-700 text-right">{customer.address}</span>
          </div>
          {/* ✅ Show Group Name Instead of Group ID */}
          <div className="flex justify-between">
            <span className="text-gray-500 font-medium">Group:</span>
            <span className="text-gray-700">
              {getGroupName(customer.group_id)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500 font-medium">Security:</span>
            <span className="text-yellow-700 font-bold">
              ₹{customer.security_amount}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500 font-medium">Advance:</span>
            <span className="text-green-600 font-bold">
              ₹ {customer.advance_amount || 0}
            </span>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-md">
            {/* Due Amount */}
            <div className="flex justify-between border-b pb-2 mb-2">
              <span className="text-gray-500 font-medium">Due Amount:</span>
              <span className="text-red-500 font-bold">
                ₹ {customer.due_amount || 0}
              </span>
            </div>

            {/* Container Balances */}
            <div className="flex justify-between items-center">
              {/* Cool Container Balance */}
              <div className="flex items-center space-x-2">
                <span className="bg-blue-200 p-2 rounded-md">🛢️</span>
                <p className="font-semibold">
                  Cool:{" "}
                  <span className="text-blue-700">
                    {customer.cool_container_balance || 0}
                  </span>
                </p>
              </div>

              {/* Pet Container Balance */}
              <div className="flex items-center space-x-2">
                <span className="bg-red-200 p-2 rounded-md">🍼</span>
                <p className="font-semibold">
                  Pet:{" "}
                  <span className="text-red-700">
                    {customer.pet_container_balance || 0}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Section */}
      <div className="w-full max-w-md mt-2 bg-white shadow-md rounded-2xl p-3">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Pay Due</h3>
        <div className="flex flex-col space-y-4">
          <input
            type="number"
            placeholder="Enter payment amount"
            value={paymentAmount}
            onChange={handlePaymentChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
          />

          {/* Payment Date */}
          <input
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
          />

          <select
            value={paymentMode}
            onChange={(e) => setPaymentMode(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
          >
            <option value="Cash">Cash</option>
            <option value="Credit">Credit</option>
            <option value="Online">Online</option>
            <option value="Cheque">Cheque</option>
          </select>
          <button
            disabled={!isPayEnabled}
            onClick={handlePayDue}
            className={`w-full py-3 rounded-lg text-white ${
              isPayEnabled
                ? "bg-blue-500 hover:bg-blue-600"
                : "bg-gray-300 cursor-not-allowed"
            }`}
          >
            Pay Due
          </button>
        </div>
      </div>

      {/* Payment History Toggle Button */}
      <div className="w-full max-w-md mt-6 flex justify-center">
        <button
          className="bg-gradient-to-r from-blue-500 to-blue-400 text-white px-6 py-3 rounded-full shadow-lg hover:shadow-xl"
          onClick={() => setShowPaymentHistory(!showPaymentHistory)}
        >
          {showPaymentHistory ? "Hide Payment History" : "View Payment History"}
        </button>
      </div>

      {/* Payment History */}
      {showPaymentHistory && (
        <div className="w-full max-w-md mt-6">
          <PaymentHistory customerId={customer.id} />
        </div>
      )}
    </div>
  );
};

export default CustomerProfile;
